'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const departments = [
  'All Departments',
  'Operations',
  'Customer Success',
  'Finance',
  'People & HR',
  'Sales',
];

const timezones = [
  'Any Timezone',
  'UTC-8 to UTC-5 (Americas)',
  'UTC-5 to UTC+0 (Atlantic)',
  'UTC+0 to UTC+3 (Europe/Africa)',
  'UTC+3 to UTC+8 (Asia/Middle East)',
  'UTC+8 to UTC+12 (Asia-Pacific)',
];

interface SearchBarProps {
  onSearch: (query: string, department: string, timezone: string) => void;
}

export default function SearchBar({ onSearch }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState('All Departments');
  const [timezone, setTimezone] = useState('Any Timezone');

  const handleSearch = () => {
    onSearch(query, department, timezone);
    const jobsSection = document.getElementById('jobs');
    if (jobsSection) {
      jobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePopularTag = (tag: string) => {
    setQuery(tag);
    onSearch(tag, department, timezone);
    const jobsSection = document.getElementById('jobs');
    if (jobsSection) {
      jobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-white border border-fog-gray rounded-xl shadow-card overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* Role input */}
        <div className="flex-1 flex items-center gap-3 px-5 py-4 border-b md:border-b-0 md:border-r border-fog-gray">
          <Icon name="MagnifyingGlassIcon" size={18} className="text-slate-light shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e?.target?.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Senior Engineer, Product Lead, Ops Director…"
            className="flex-1 text-sm font-medium text-slate-dark bg-transparent placeholder:text-slate-light/70 border-none outline-none"
          />
        </div>

        {/* Department */}
        <div className="flex items-center gap-2 px-5 py-4 border-b md:border-b-0 md:border-r border-fog-gray min-w-0 md:w-48">
          <Icon name="BriefcaseIcon" size={16} className="text-slate-light shrink-0" />
          <select
            value={department}
            onChange={(e) => setDepartment(e?.target?.value)}
            className="flex-1 text-sm font-medium text-slate-pencil bg-transparent border-none outline-none appearance-none cursor-pointer"
          >
            {departments?.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        {/* Timezone */}
        <div className="flex items-center gap-2 px-5 py-4 border-b md:border-b-0 md:border-r border-fog-gray min-w-0 md:w-52">
          <Icon name="GlobeAltIcon" size={16} className="text-slate-light shrink-0" />
          <select
            value={timezone}
            onChange={(e) => setTimezone(e?.target?.value)}
            className="flex-1 text-sm font-medium text-slate-pencil bg-transparent border-none outline-none appearance-none cursor-pointer"
          >
            {timezones?.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        {/* CTA */}
        <button
          id="get-matched"
          onClick={handleSearch}
          className="flex items-center justify-center gap-2 bg-signal-blue text-white text-sm font-semibold px-8 py-4 hover:bg-blue-600 active:bg-blue-700 transition-colors shrink-0 cursor-pointer"
        >
          Get Matched Weekly
          <Icon name="ArrowRightIcon" size={16} />
        </button>
      </div>
      {/* Popular queries */}
      <div className="px-5 py-3 border-t border-fog-gray/60 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-light font-medium">Popular:</span>
        {['Data Entry', 'Customer Service', 'Virtual Assistant', 'Transcription', 'Live Chat']?.map((tag) => (
          <button
            key={tag}
            onClick={() => handlePopularTag(tag)}
            className="text-xs font-medium text-slate-pencil bg-fog-gray/50 hover:bg-signal-blue/10 hover:text-signal-blue px-2.5 py-1 rounded-md transition-colors"
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}